import { useEffect, useMemo, useState } from "react";
import type { Data } from "@puckeditor/core";
import { ArrowDown, ArrowUp, Plus, Sparkles, Trash2, Wand2 } from "lucide-react";
import { useStylePanel } from "./style-panel-context";
import { MobileEditorSheet } from "./mobile-editor-sheet";
import {
  buildQuickFields,
  getSectionContentBrief,
} from "./simplified-editor-guidance";

interface SimplifiedEditorProps {
  data: Data;
  onDataChange: (data: Data) => void;
  onOpenSectionLibrary: () => void;
  onOpenFullControls: () => void;
}

type EditorBlock = {
  type: string;
  props: Record<string, unknown>;
} & Record<string, unknown>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function isEditorBlock(value: unknown): value is EditorBlock {
  return isRecord(value) && typeof value.type === "string" && isRecord(value.props);
}

function updateBlock(
  data: Data,
  componentId: string,
  updater: (item: EditorBlock, props: Record<string, unknown>) => EditorBlock,
): Data {
  const content = Array.isArray(data.content) ? data.content.filter(isEditorBlock) : [];

  return {
    ...data,
    content: content.map((entry) => {
      if (entry.props.id !== componentId) {
        return entry;
      }

      return updater(entry, entry.props);
    }) as Data["content"],
  };
}

function reorder<T>(items: T[], from: number, to: number): T[] {
  const next = [...items];
  const [item] = next.splice(from, 1);
  if (item === undefined) {
    return items;
  }
  next.splice(to, 0, item);
  return next;
}

export function SimplifiedEditor({
  data,
  onDataChange,
  onOpenSectionLibrary,
  onOpenFullControls,
}: SimplifiedEditorProps) {
  const { selectedComponentId, selectedComponentType } = useStylePanel();
  const [isMobile, setIsMobile] = useState(false);

  const content = useMemo(
    () => (Array.isArray(data.content) ? data.content : []).filter(isEditorBlock),
    [data.content],
  );

  const selectedIndex = useMemo(
    () => content.findIndex((entry) => {
      const props = isRecord(entry.props) ? entry.props : {};
      return props.id === selectedComponentId;
    }),
    [content, selectedComponentId],
  );

  const selectedEntry = selectedIndex >= 0 ? content[selectedIndex] : null;
  const selectedProps = selectedEntry && isRecord(selectedEntry.props) ? selectedEntry.props : {};
  const selectedLabel = selectedComponentType ?? "Section";
  const quickFields = useMemo(
    () => buildQuickFields(selectedEntry?.type, selectedProps),
    [selectedEntry?.type, selectedProps],
  );
  const sectionBrief = useMemo(
    () => getSectionContentBrief(selectedEntry?.type, selectedProps),
    [selectedEntry?.type, selectedProps],
  );

  useEffect(() => {
    const syncViewport = () => {
      setIsMobile(window.innerWidth < 768);
    };

    syncViewport();
    window.addEventListener("resize", syncViewport);
    return () => window.removeEventListener("resize", syncViewport);
  }, []);

  const handleFieldChange = (key: string, value: string) => {
    if (!selectedComponentId) {
      return;
    }

    onDataChange(updateBlock(data, selectedComponentId, (entry, props) => ({
      ...entry,
      props: {
        ...props,
        [key]: value,
      },
    })));
  };

  const handleMove = (direction: -1 | 1) => {
    if (selectedIndex < 0) {
      return;
    }

    const nextIndex = selectedIndex + direction;
    if (nextIndex < 0 || nextIndex >= content.length) {
      return;
    }

    onDataChange({
      ...data,
      content: reorder(content, selectedIndex, nextIndex) as Data["content"],
    });
  };

  const handleDelete = () => {
    if (!selectedComponentId) {
      return;
    }

    onDataChange({
      ...data,
      content: content.filter((entry) => entry.props.id !== selectedComponentId) as Data["content"],
    });
  };

  if (!selectedComponentId || selectedIndex < 0) {
    return null;
  }

  const moveDirection = selectedIndex < content.length - 1 ? 1 : selectedIndex > 0 ? -1 : 0;
  const MoveIcon = moveDirection < 0 ? ArrowUp : ArrowDown;
  const moveLabel = moveDirection < 0 ? "Move up" : "Move down";

  if (isMobile) {
    return (
      <MobileEditorSheet
        sectionLabel={selectedLabel}
        canMove={moveDirection !== 0}
        moveDirection={moveDirection < 0 ? "up" : "down"}
        quickFields={quickFields}
        sectionBrief={sectionBrief}
        onFieldChange={handleFieldChange}
        onMove={() => handleMove(moveDirection as -1 | 1)}
        onDelete={handleDelete}
        onAddSection={onOpenSectionLibrary}
        onOpenFullControls={onOpenFullControls}
      />
    );
  }

  return (
    <div className="pointer-events-none absolute bottom-6 left-6 z-30 max-w-sm">
      <div className="pointer-events-auto rounded-[24px] border border-border bg-background/95 p-5 shadow-xl backdrop-blur">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">Quick section edit</p>
            <h3 className="mt-2 text-lg font-semibold text-foreground">{selectedLabel}</h3>
            <p className="mt-1 text-sm leading-6 text-muted-foreground">
              Click any visible section, update its main copy here, then open full controls for deeper layout and styling.
            </p>
          </div>
          <button
            type="button"
            onClick={onOpenSectionLibrary}
            className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-border bg-card text-foreground transition hover:bg-accent"
            title="Add section"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>

        {quickFields.length > 0 ? (
          <div className="mt-4 space-y-3">
            {quickFields.map((field) => (
              <label key={field.key} className="block space-y-1.5">
                <span className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">{field.label}</span>
                <textarea
                  value={field.value}
                  rows={field.value.length > 120 ? 4 : 2}
                  onChange={(event) => handleFieldChange(field.key, event.target.value)}
                  className="min-h-12 w-full rounded-2xl border border-input bg-background px-3 py-2.5 text-sm text-foreground outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15"
                />
              </label>
            ))}
          </div>
        ) : (
          <div className="mt-4 rounded-2xl border border-dashed border-border bg-card px-4 py-3 text-sm text-muted-foreground">
            This section does not expose quick text fields yet. Use full controls for advanced editing.
          </div>
        )}

        {sectionBrief ? (
          <div className="mt-4 rounded-2xl border border-[var(--color-ai)]/20 bg-[var(--color-ai)]/5 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-ai)]">
              {sectionBrief.title}
            </p>
            <p className="mt-2 text-sm leading-6 text-foreground">{sectionBrief.instruction}</p>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">{sectionBrief.example}</p>
          </div>
        ) : null}

        <div className="mt-4 grid grid-cols-3 gap-2" data-simplified-section-actions="3">
          <button
            type="button"
            onClick={() => handleMove(moveDirection as -1 | 1)}
            disabled={moveDirection === 0}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl border border-border bg-card px-3 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent disabled:opacity-50"
          >
            <MoveIcon className="h-4 w-4" />
            {moveLabel}
          </button>
          <button
            type="button"
            onClick={handleDelete}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl border border-destructive/25 bg-destructive/5 px-3 py-2.5 text-sm font-medium text-destructive transition hover:bg-destructive/10"
          >
            <Trash2 className="h-4 w-4" />
            Remove
          </button>
          <button
            type="button"
            onClick={onOpenFullControls}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-primary px-3 py-2.5 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
          >
            <Wand2 className="h-4 w-4" />
            Full controls
          </button>
        </div>

        <button
          type="button"
          onClick={onOpenFullControls}
          className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
        >
          <Sparkles className="h-4 w-4" />
          Need more than quick edits? Open the full editor.
        </button>
      </div>
    </div>
  );
}
